<template>
    <div class="compliance-item-detail">
        
        <div v-if="hasStatusObj" class="compliance-item-detail-sections">

            <div class="section-item">
                <div class="w-100 d-flex align-items-center justify-content-between gap-3">
                    <div class="">
                        <h3 class="mb-0">{{ statusObj.title }}</h3>
                        <h6 class="text-muted mt-2 mb-0">{{ statusObj.vendor.name }}</h6>
                    </div>
                    <div class="d-flex gap-2 align-items-center">
                        <b-button v-if="removeEmptyVciTransition" variant="subtle-secondary" size="sm" @click="startTransition(removeEmptyVciTransition)">
                            <b-icon icon="trash-fill" variant="danger" aria-hidden="true"></b-icon>
                            Remove
                        </b-button>
                        <b-dropdown text="Actions" variant="subtle-secondary" size="sm" right @show="loadTransitions">
                            <template #button-content>
                                <b-icon icon="lightning-charge-fill" variant="warning" aria-hidden="true"></b-icon>
                                Actions
                            </template>
                            <!-- set all to the same width, and less than the max for the dropdown -->
                            <template v-if="!transitionsLoaded || loadingTransitions">
                                <b-dropdown-item disabled class="width-300">
                                    <b-spinner small class="mr-1"></b-spinner>
                                    Loading...
                                </b-dropdown-item>
                            </template>
                            <template v-else-if="actionTransitions.length === 0">
                                <b-dropdown-item disabled class="width-300 text-muted special-text font-size-70">No Actions Available</b-dropdown-item>
                            </template>
                            <template v-else>
                                <b-dropdown-item 
                                    v-for="trans in actionTransitions" 
                                    :key="trans.trigger"
                                    link-class="px-2 d-flex align-items-center gap-2" 
                                    class="width-300"
                                    @click="startTransition(trans)"
                                >
                                    <b-icon 
                                        :icon="trans.icon" 
                                        :variant="trans.color_variant" 
                                        class="rounded-circle" 
                                        :class="['bg-' + (trans.color_variant || 'primary') + '-100']" 
                                        style="padding: 6px;" 
                                        font-scale="2"
                                    ></b-icon>
                                    <div class="text-wrap line-height-110">
                                        <div class="font-weight-bold font-size-80">{{ trans.label }}</div>
                                        <div class="text-muted font-size-70">{{ trans.description }}</div>
                                    </div>
                                </b-dropdown-item>
                            </template>
                        </b-dropdown>
                    </div>
                </div>
            </div>

            <div class="section-item">
                <div class="w-100 d-flex flex-row gap-3 justify-content-between">
                    <div class="" style="max-width: 35%;">
                        <h6 class="item-heading">Kind</h6>
                        <p class="mb-0 item-text"><CompKindChip :kind="localCompItem.kind"></CompKindChip></p>
                    </div>
                    <div class="" style="max-width: 35%;">
                        <h6 class="item-heading">Category</h6>
                        <p class="mb-0 item-text">{{ localCompItem.item_type.name }}</p>
                    </div>
                    <div class="" style="max-width: 35%;">
                        <h6 class="item-heading">Created</h6>
                        <p class="mb-0 item-text">{{ formatDate(localVendorCompItem.created, '-') }}</p>
                    </div>
                    <div class="" style="max-width: 35%;">
                        <h6 class="item-heading">Last Modified</h6>
                        <p class="mb-0 item-text">{{ formatDate(localVendorCompItem.modified, '-') }}</p>
                    </div>
                </div>
            </div>

            <!-- <div class="section-item">
                <div class="w-100 rounded-lg p-2 d-flex align-items-start justify-content-between">
                    <div class="w-50">
                        <h6 class="item-heading">Created</h6>
                        <p class="mb-0 item-text">{{ formatDate(localCompItem.created, '-') }}</p>
                    </div>
                    <div class="w-50">
                        <h6 class="item-heading">Last Modified</h6>
                        <p class="mb-0 item-text">{{ formatDate(localCompItem.modified, '-') }}</p>
                    </div>
                </div>
            </div> -->

            <div class="section-item">
                <div class="w-100">
                    <div class="mb-2 d-flex align-items-center">
                        <h6 class="item-heading mb-0">Overall Status</h6>
                        <b-icon 
                            icon="info-circle" 
                            variant="secondary" 
                            v-b-popover.hover="`The overall status is based on the status of all versions of this document combined.`" 
                            class="ml-2"
                        ></b-icon>
                    </div>
                    <div class="w-100 d-flex">
                        <div class="w-100 border rounded-lg px-3 py-2 d-flex align-items-center justify-content-between bg-white">
                            <div class="w-20 d-flex align-items-center justify-content-center">
                                <b-badge
                                    :variant="statusObj.overall_status.variant"
                                    class="text-uppercase font-size-100"
                                    v-b-tooltip.hover.top.window
                                    :title="statusObj.overall_status.description"
                                >
                                    {{ statusObj.overall_status.label }}
                                </b-badge>
                            </div>
                            <div class="w-40 text-center">
                                <h6 class="item-heading">Valid From</h6>
                                <p class="mb-0 item-text">{{ formatDate(statusObj.valid_from_date_tenant_tz, 'Not Set') }}</p>
                            </div>
                            <div class="w-10 text-center item-text">
                                &rarr;
                            </div>
                            <div class="w-40 text-center">
                                <h6 class="item-heading">{{ isFutureDate(statusObj.valid_to) ? 'Expires' : 'Expired' }} On</h6>
                                <p class="mb-0 item-text">{{ formatDate(statusObj.valid_to_date_tenant_tz, 'Not Set') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="currentTuIsInternal" class="section-item">
                <div class="w-100">
                    <h6 class="item-heading">Internal Notes</h6>
                    <!-- these are the notes on the VCI -- we have separate notes on the CompItem/CompItemScope (for item-level stuff) and CompSub for version-level stuff -->
                    <div class="mb-0 item-text text-show-breaks">{{ localVendorCompItem.internal_notes || '-' }}</div>
                </div>
            </div>

            <!-- HIDING THESE FOR NOW - they come from CompItem/CompItemScope -- should make it clear where they come from -->
            <!-- <div v-if="localVendorCompItem.instructions" class="section-item">
                <div class="w-100">
                    <h6 class="item-heading">Instructions for Suppliers</h6>
                    <div class="mb-0 item-text text-show-breaks">{{ localVendorCompItem.instructions || '-' }}</div>
                </div>
            </div> -->

            <div class="section-item">
                <div class="w-100">
                    <div class="section-heading-outer mb-2 d-flex align-items-center">
                        <h6 class="section-heading mb-0">Versions</h6>
                        <b-icon 
                            icon="info-circle" 
                            variant="secondary" 
                            v-b-popover.hover="`Track multiple versions of the same document, if the document is renewed or revised.`" 
                            class="ml-2"
                        ></b-icon>
                    </div>
                    <div class="d-flex flex-column gap-3">
                        <div v-if="!orderedSubmissions.length" class="text-muted text-center font-size-80">
                            No versions of this document have been added yet.
                        </div>
                        <CompSubSingle
                            v-for="sub in orderedSubmissions"
                            :key="sub.id"
                            :sub="sub"
                            class="border rounded-lg px-2 py-2 shadow-sm"
                            :class="sub._localClasses"
                            @updated="handleSubUpdated"
                        ></CompSubSingle>
                    </div>
                    <div class="mt-2">
                        <b-button
                            v-if="!addSubShowing && canAddSubmission"
                            key="addSubButton"
                            variant="subtle-primary"
                            size="sm"
                            :disabled="!canAddSubmission || loading"
                            @click="startAddSubmission"
                        >
                            + Add New Version
                        </b-button>
                        <!-- EXPERIMENTAL START NODE BUTTON
                        DO NOT USE... should really just be controlled by transitions on VCI (or an actual transition on VCI with form and everything) 
                        -->
                        <!-- <b-button
                            v-if="!addSubShowing && compSubStartNodes.length > 0"
                            key="compSubStarterButton"
                            variant="subtle-primary"
                            size="sm"
                            :disabled="loading"
                            @click="openCompSubStarter"
                        >
                            + Upload New Version
                        </b-button> -->
                        <div v-if="addSubShowing" key="addSubFormArea" class="border rounded-lg px-3 py-2 bg-white shadow-sm">
                            <h5>Add new version</h5>
                            <hr>
                            <CompSubForm
                                :vendorCompItemId="vendorCompItemId"
                                :fileRequired="localCompItem.comp_subs_file_required"
                                @cancel="cancelAddSubmission"
                                @saved="handleAddSubmissionSaved"
                            ></CompSubForm>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="nodesLoaded && allActiveNodes.length"
                class="section-item flex-column"
            >
                <div class="section-heading-outer w-100 mb-2 d-flex align-items-center">
                    <h6 class="section-heading mb-0">Active Tasks</h6>
                    <b-icon 
                        icon="info-circle" 
                        variant="secondary" 
                        v-b-popover.hover="`Active tasks related to this Document`" 
                        class="ml-2"
                    ></b-icon>
                </div>

                <div class="w-100 d-flex flex-column gap-5">
                    <div class="w-100">
                        <!-- SHOW LIST OF NODES HERE 
                        VCI tasks are never interactive?  just instructions for doing something on CompSub? (or override their status, etc.)
                        need to have a thing that checks what VCI nodes are active and which ones can be completed (we have that... in EAS stuff)
                        -->
                        <div class="d-flex flex-column gap-1">
                            <div 
                                v-for="node in allActiveNodes"
                                :key="node.id"
                                class="font-size-70 w-100 border rounded bg-gray-50 px-3 py-2 d-flex justify-content-between gap-3"
                            >
                                <div class="">
                                    <div class="font-weight-500">
                                        {{ node.title }}
                                    </div>
                                    <div v-if="node.description" class="text-muted mt-1">{{ node.description }}</div>
                                </div>
                                <div class="">
                                    <ActiveAssigneeChip
                                        v-if="node.assigned_tus.map(t => t.id).includes(currentTuId)"
                                        variant="warning"
                                        text="You"
                                        tooltip="Waiting on your action"
                                        class="ml-2"
                                    ></ActiveAssigneeChip>
                                    <!-- Put other info here like deadline, task type? -->
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="currentTuIsInternal" class="section-item flex-column">
                <!-- only show for internals... almost nothing meaningful will load for externals anyways, so just hide the section -->
                <div class="section-heading-outer w-100 mb-2 d-flex align-items-center">
                    <h6 class="section-heading mb-0">Reminders &amp; Automations</h6>
                    <b-icon 
                        icon="info-circle" 
                        variant="secondary" 
                        v-b-popover.hover="`Reminder tasks and other automations triggered based on the document expiration and status.`" 
                        class="ml-2"
                    ></b-icon>
                </div>

                <div class="w-100 d-flex flex-column gap-5">
                    <!-- <div v-if="nodesLoaded && activeEasNodes.length" class="w-100">
                        <div class="mb-2 d-flex align-items-center">
                            <h6 class="item-heading mb-0">Active Reminders</h6>
                            <b-icon 
                                icon="info-circle" 
                                variant="secondary" 
                                v-b-popover.hover="`Currently active reminders.`" 
                                class="ml-2"
                            ></b-icon>
                        </div>
                        <div class="d-flex flex-column gap-1">
                            <div 
                                v-for="node in activeEasNodes"
                                :key="node.id"
                                class="font-size-70 w-100 border rounded bg-gray-50 px-3 py-1"
                            >
                                <div class="w-100 font-size-70 d-flex align-items-center justify-content-center">
                                    <b-iconstack class="mr-1" font-scale="1.25">
                                        <b-icon icon="circle-fill" class="text-primary-200"></b-icon>
                                        <b-icon icon="circle-fill" animation="throb" variant="primary" scale="0.5"></b-icon>
                                    </b-iconstack>
                                    <div class="font-weight-500">{{ node.title }}</div>
                                </div>
                                <div v-if="node.description" class="text-muted mt-1 font-size-70 text-center">{{ node.description }}</div>
                            </div>
                        </div>
                    </div> -->
                    
                    <b-alert :show="hasExpireActionSpecs && !hasExpireDate" variant="danger" class="font-size-80 mb-0">
                        Reminder actions will not be triggered if there is no expiration date set for the active version of this document.
                    </b-alert>
                    
                    <!-- CompItem scoped EAS - now combined with VCI ones -->
                    <!-- <div class="">
                        <div class="mb-2 d-flex align-items-center">
                            <h6 class="item-heading mb-0">Scheduled Automations - from Document Type</h6>
                            <b-icon 
                                icon="info-circle" 
                                variant="secondary" 
                                v-b-popover.hover="`These automations are set for all Documents of this Document Type, including this one.  To change these, go to the Settings area for the Document Type.`" 
                                class="ml-2"
                            ></b-icon>
                        </div>
                        <ExpireActionManager
                            :compItemId="localCompItemId" 
                            :readOnly="true"
                            :narrow="true"
                        ></ExpireActionManager>
                    </div> -->
                    <div class="">
                        <!-- VCI scoped EAS -->
                        <div class="mb-2 d-flex align-items-center">
                            <h6 class="item-heading mb-0">Existing Automations</h6>
                            <b-icon 
                                icon="info-circle" 
                                variant="secondary" 
                                v-b-popover.hover="`Reminders and automations that are already set for this document, or for all documents of this type.  These will be triggered based on the expiration status of all versions of this document combined.`" 
                                class="ml-2"
                            ></b-icon>
                        </div>
                        <ExpireActionManager 
                            :compItemId="localCompItemId" 
                            :vendorCompItemId="vendorCompItemId" 
                            :noAdd="!hasExpireDate"
                            :noAddMessage="'No reminders allowed - No expiration date set'"
                            :narrow="true"
                            @updated="handleExpireActionManagerUpdated"
                        ></ExpireActionManager>
                    </div>
                </div>
            </div>

            <b-modal
                v-model="transModalShowing"
                centered
                hide-footer
                size="md"
                @hidden="afterTransModalHidden"
            >
                <FlowObjectTransitionForm
                    v-if="activeTransition.trigger"
                    :key="activeTransition.trigger"
                    :flowObject="vciFlowObject"
                    :trigger="activeTransition.trigger"
                    :hideCancel="false"
                    :autoStart="true"
                    :noReloadOnSuccess="true"
                    :minHeight="'150px'"
                    @loading-start="handleTransitionFormEvent"
                    @loading-end="handleTransitionFormEvent"
                    @loading-error="handleTransitionFormEvent"
                    @transition-start="handleTransitionFormEvent"
                    @transition-error="handleTransitionFormEvent"
                    @transition-success="handleTransitionFormEvent"
                    @transition-end="handleTransitionFormEvent"
                    @cancel="handleTransitionFormCancel"
                ></FlowObjectTransitionForm>
        </b-modal>

        <!-- <flow-object-starter
            ref="compSubStarter"
            flow-object-class="ComplianceSubmission"
            :flow-subtype="vendorCompItemId"
        ></flow-object-starter> -->

        </div>

    </div>
</template>

<script>
import { 
    suppliosApi, 
    // getFlowObjectApi,
} from '@/js/api-service';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import CompKindChip from './shared/CompKindChip.vue';
import { FormatDateHelper, luxonDurationToHumanizedString, isFutureDate } from '@/js/filters-helpers';
import { Duration } from 'luxon';
// import StaffDebug from '../shared/StaffDebug.vue';
import ExpireActionManager from './ExpireActionManager.vue';
import { FlowObject } from '@/components/flows/models/flowObject';
import { VendorCompItemStatus } from './shared/compModels';
import CompSubForm from './CompSubForm.vue';
import CompSubSingle from './comp_sub/CompSubSingle.vue';
import FlowObjectTransitionForm from '@/components/flows/components/FlowObjectTransitionForm.vue';
// import { nanoid } from 'nanoid';
// import cloneDeep from 'lodash/cloneDeep';
// import sortBy from 'lodash/sortBy';
import ActiveAssigneeChip from './shared/ActiveAssigneeChip.vue';

// TODO - MUST UPDATE THIS TO USE FLOW OBJECT OF VCI, NOT COMP ITEM

const REMOVE_EMPTY_VCI_TRIGGER = 'remove_empty_vci';

export default {
    name: 'VendorCompItemDetail',
    components: {
        CompKindChip,
        // StaffDebug,
        ExpireActionManager,
        CompSubForm,
        CompSubSingle,
        ActiveAssigneeChip,
        FlowObjectTransitionForm,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        vendorCompItemId: {
            type: String,
            required: true,  // make false later when we support CompItem/Vendor combos
        },
        // TODO - update this to work with just compItemId / vendorId combos! - add props, update all things, make it call different endpoints?  (and/or block some)
    },
    data() {
        return {
            statusObj: {},  // VendorCompItemStatus object
            addSubShowing: false,
            loading: false,
            vciFlowObject: null,  // set in created hook -- this is for the VendorCompItem itself
            // for calling transitions on VCI
            transModalShowing: false,
            activeTransition: {},  // the active transition object, set when we start a transition
            compSubStartNodes: [],
        }
    },
    computed: {
        vendorId() {
            return this.statusObj?.vendor_id || '';
        },
        localCompItem() {
            return this.statusObj?.compItem || {};
        },
        localCompItemId() {
            // use this if you need to pass the ID to a child component, as we will always have it right away
            return this.compItemId || this.compItem?.id || this.localCompItem?.id || '';
        },
        localVendorCompItem() {
            return this.statusObj?.vendorCompItem || {};
        },
        hasStatusObj() {  // loaded yet?
            return !!this.statusObj?.id;
        },
        hasLocalCompItem() {
            return !!this.localCompItem?.id;
        },
        hasExpireDate() {
            return !!this.statusObj?.valid_to;
        },
        hasExpireActionSpecs() {  // NOTE: the expire action manager actually manages the expire_action_specs -- but the number from statusObj should be correct since it refreshes
            return this.statusObj?.hasExpireActionSpecs;
        },
        //from vciFlowObject - which is a VendorCompItem here
        // vciFlowObjectLoaded() {  // replaced by nodesLoaded (checks flowObject.isNodesLoaded)
        //     //return Object.keys(this.reqData).length > 0;
        //     return this.vciFlowObject.hasData
        // },
        // vciFlowObjectData() {  // not used, because we get VCI status object instead
        //     return this.vciFlowObject.data;
        // },
        nodesLoaded() {
            return this.vciFlowObject.isNodesLoaded;
        },
        nodesByZone() {
            return this.vciFlowObject.getNodesByZone();
        },
        taskNodes() {
            return this.vciFlowObject.getNodesForZone('tasks');
        },
        activeEasNodes() {
            // TODO - make this less fragile!
            return this.vciFlowObject.getNodesForZone('tasks').filter(n => n.is_active && n.key.startsWith('followup_reminder'));
        },
        allActiveNodes() {
            // active nodes for ALL assignees (will only include the ones visible to current TU)
            return this.vciFlowObject.getNodes({
                onlyActive: true,
            });
        },
        assignedActiveNodes() {
            // return the active nodes that are assigned to the current TU - will there be any nodes on VCI?
            return this.vciFlowObject.getNodes({
                assignedTuId: this.currentTuId,
                onlyActive: true,
            });
        },
        // TRANSITIONS
        transitionsLoaded() {
            return this.vciFlowObject.isTransitionsLoaded;
        },
        loadingTransitions() {
            return this.vciFlowObject.loadingTransitions;
        },
        hasActiveTransition() {
            return !!this.vciFlowObject.activeTransition;
        },
        transitionsByZone() {
            return this.vciFlowObject.getTransitionsByZone();
        },
        actionTransitions() {
            return this.vciFlowObject.getTransitionsForZone('actions');
        },
        removeEmptyVciTransition() {
            return this.vciFlowObject.getTransitions({ trigger: REMOVE_EMPTY_VCI_TRIGGER })[0];  // assuming we only care about the first one
        },
        possibleTriggerNames() {
            // return this.transitions.map((transition) => {
            //     return transition.trigger;
            // });
            return this.vciFlowObject.getPossibleTriggerNames();
        },
        // SUBMISSIONS
        canAddSubmission() {
            // can add submission if we are a full user or tenant admin
            // TODO - must change this to allow external users to add submissions if they have the right permissions!
            // check available transitions?
            const allowTriggers = ['allow_create_sub_internal'];  // when you're ready to add supplier capability for this, add 'allow_create_sub_external'
            return allowTriggers.some((t) => this.possibleTriggerNames.includes(t));  // we don't use this for actually creating the submission, just to control the button visibility
        },
        // SUBMISSIONS
        primarySubmissionId() {
            return this.statusObj?.primary_submission_id || '';
        },
        secondarySubmissionId() {
            return this.statusObj?.secondary_submission_id || '';
        },
        orderedSubmissions() {
            // our local, specially ordered list of submissions
            // if there is a new one pending, we put that FIRST, then any 'current' one next, then the others
            // if nothing pending, we put the primary first, then secondary, then the rest
            const subs = this.statusObj?.submissions || [];
            console.log('Original subs when making orderedSubmissions:', subs);
            let inProgressSubs = [];
            let primarySubNotInProgress = null;
            let secondarySubNotInProgress = null;
            let otherSubs = [];
            subs.forEach((sub) => {
                const subCopy = {...sub};  // so we can add some extra local stuff.... maybe add this to the JS class we have...
                // or just add classes in the template... putting here instead of template
                subCopy._localClasses = {
                    'border-primary': sub.is_in_progress || false,
                    'bg-white': sub.is_valid || sub.is_in_progress,
                    'bg-gray-100': !sub.is_valid && !sub.is_in_progress,
                };
                if (sub.is_in_progress) {
                    inProgressSubs.push(subCopy);  // continue...
                } else if (sub.id === this.primarySubmissionId) {
                    primarySubNotInProgress = subCopy;
                } else if (sub.id === this.secondarySubmissionId) {
                    secondarySubNotInProgress = subCopy;
                } else {
                    otherSubs.push(subCopy);
                }
            });
            const orderedSubs = [...inProgressSubs, primarySubNotInProgress, secondarySubNotInProgress, ...otherSubs].filter(s => !!s);  // filter out any nulls
            return orderedSubs;
        },
        currentTuId() {
            return this.$currentUserInfo?.tenant_user_id || '';
        },
        currentTuIsInternal() {
            return !!this.$currentUserInfo?.is_internal;
        },
        currentTuIsExternal() {
            // we don't check for explicit external... in case we don't get the currentUserInfo somehow... so we just check for NOT internal, which is safer
            return !this.currentTuIsInternal;
        },
    },
    created() {
        // this.getStatus();
        this.vciFlowObject = new FlowObject({id: this.vendorCompItemId, model_class: 'VendorCompItem', data: {}});  // set in created hook?
    },
    mounted() {
        // this.vciFlowObject?.fetchAll();  // this is async -- just need nodes instead???  probably transitions too.... just get all for now
        // TEMP
        console.log('VendorCompItemDetail mounted for vendorCompItemId', this.vendorCompItemId);
        // window.vendorCompItemDetail = this;
        this.refresh();  // just fire and forget to load all the things
        // this.getCompSubStartNodes();  // fire and forget TESTING
    },
    methods: {
        async refresh() {
            // TODO - are there data things we need to reset here?  like activeTransition?
            // recreate the FlowObject?  
            return Promise.all([
                this.getStatus(),
                this.loadNodes(),  // load nodes from the flow object
                this.loadTransitions(),  // load transitions from the flow object
            ]);
        },
        async loadNodes() {
            await this.vciFlowObject.fetchNodes();
            // we don't need to stash them, we have computed properties that get from the flowObject
        },
        async loadTransitions() {
            await this.vciFlowObject.fetchTransitions();
            // we don't need to stash them, we have computed properties that get from the flowObject
            // we also call this each time the dropdown is clicked, to refresh the list
        },
        async getStatus() {
            // get the compItem from the API
            // set it to localCompItem
            this.loading = true;
            let resp;
            try {
                resp = await suppliosApi.vendorCompItemStd.get_item_status(this.vendorCompItemId);
                this.statusObj = new VendorCompItemStatus(resp.data);  // resp.data;
                // IF USING FLOW OBJECT ATTACHED TO EACH SUB -- do something like this long-term (but in separate child components)
            } catch (e) {
                this.apiErrorHandler(e);
            }
            console.log(`Finished getStatus for vendorCompItemId ${this.vendorCompItemId}`);
            this.loading = false;
        },
        formatDate(dateString, defaultText = '-') {
            return dateString ? FormatDateHelper(dateString) : defaultText;
        },
        isFutureDate(dateString) {
            if (!dateString) {
                return true;  // we return true here so things say 'Expires' if not set
            }
            return isFutureDate(dateString);
        },
        get_eas_text(easData) {
            // return a string with the text of the easData
            // this will be a computed property on the EAS model
            const durationObj = {
                days: easData.duration_days,
                weeks: easData.duration_weeks,
                months: easData.duration_months,
            };
            const luxonDuration = Duration.fromObject(durationObj);
            const durationString = luxonDurationToHumanizedString(luxonDuration);
            return `Will start reminder task ${durationString} before expiration`;
        },
        startAddSubmission() {
            // open the modal/collapse to add a new submission
            // set initial data
            // on save, refresh the whole thing
            console.log('startAddSubmission');
            this.addSubShowing = true;
        },
        cancelAddSubmission() {
            this.addSubShowing = false;
        },
        handleAddSubmissionSaved() {
            this.addSubShowing = false;
            this.refresh();  // await this?  so we can pass up new data in emit?
            this.emitUpdated();
        },
        handleExpireActionManagerUpdated() {
            this.refresh();  // await this?  so we can pass up new data in emit?
            this.emitUpdated();
        },
        getEmitPayload() {  // standard payload for emitting events related to this VendorCompItem
            return {
                id: this.vendorCompItemId,
                vendor_id: this.vendorId,
                comp_item_id: this.localCompItemId,
                vendor_comp_item_id: this.vendorCompItemId,
            };
        },
        emitUpdated() {
            // tell parent we updated something so it can refresh all
            // payload?  vendorCompItemId?  comp_item_id?  vendor_id?  we won't always have submissions, they could have changed, etc.
            // should we emit what we just updated?  like the transition that was called?  make sure the payload is standardized...
            const payload = this.getEmitPayload();
            this.$emit('updated', payload);
        },
        emitDeleted() {
            // the parent we were deleted... by the time we emit this, the VCI will either be hard or soft deleted
            // so parent cannot refetch US directly... but could refresh the list of VendorCompItems or the line that this thing was in
            // we will try to keep the same payload structure as emitUpdated
            const payload = this.getEmitPayload();  // but it won't exist in the DB anymore... but parent might still be tracking us as this
            this.$emit('deleted', payload);
        },
        // HANDLING SUBMISSIONS (from child components)
        handleSubUpdated(payload) {
            // handles flow transition or node transition updates from CompSubSingle
            // this means something important changed on the submission, so we refresh the whole thing here
            // NOTE: it could mean that the submission is no longer visible.... if removed.
            console.log('handleSubUpdated', payload);
            this.refresh();
            this.emitUpdated();  // tell parent we updated something so it can refresh all
        },
        // WORKING WITH TRANSITIONS
        async startTransition(transObj) {
            // start a transition -- will open the modal and transition form component will handle it
            console.log('startTransition', transObj);
            // const triggerName = transObj.trigger;
            this.activeTransition = transObj;  // set the active transition trigger
            this.transModalShowing = true;  // show the modal
        },
        resetTransition() {
            // utility to reset everything related to submission transition
            this.activeTransition = {};  // reset the active transition
        },
        closeTransitionModal() {
            // close the transition modal
            this.transModalShowing = false;
            this.resetTransition();  // reset the transition
        },
        afterTransModalHidden() {
            this.resetTransition();
        },
        handleTransitionFormCancel(payload) {
            console.log('handleTransitionFormCancel', payload);
            this.closeTransitionModal();
        },
        handleTransitionFormEvent(payload) {
            console.log('handleTransitionFormEvent', payload);
            const { event, trigger, activeTransition, error, responseData } = payload;
            // this thing handles ALL events... so we need to check the event type and do something with it
            // this.activeTransition = activeTransition; // make sure this plays nice with other stuff?  how to make sure we don't collide?
            console.log('handleTransitionFormEvent - event, trigger, activeTransition, error, responseData', event, trigger, activeTransition, error, responseData);
            if (event === 'transition-success') {
                // tell parent to refresh stuff?
                this.closeTransitionModal();
                if (trigger === REMOVE_EMPTY_VCI_TRIGGER) {
                    this.emitDeleted();
                } else {
                    // as long as we didn't delete ourselves, then we refresh, tell parent and let it handle what to do
                    this.refresh();  // await this?  so we can pass up new data in emit?
                    // this.$emit('updated');  // what payload to include?
                    this.emitUpdated();  // tell parent we updated something so it can refresh all
                }
            }
        },
        // START NODES FOR NEW COMP SUB
        // async getCompSubStartNodes() {
        //     // NO... use transition on VCI for these things instead (anytime submit)
        //     const modelClass = 'ComplianceSubmission';
        //     const flowSubtype = this.vendorCompItemId;
        //     console.log('Starting getCompSubStartNodes - modelClass, subtype:', modelClass, flowSubtype);
        //     const compSubApi = getFlowObjectApi(modelClass, flowSubtype);
        //     let resp;
        //     try {
        //         resp = await compSubApi.get_start_nodes();
        //     } catch (error) {
        //         console.error('problem getting CompSub start nodes in VendorCompItemDetail', error);
        //     }
        //     if (resp?.data) {
        //         console.log('Got CompSub start nodes', resp.data);
        //         this.compSubStartNodes = resp.data;
        //     }
        // },
        // openCompSubStarter() {
        //     const starter = this.$refs.compSubStarter;
        //     if (starter) {
        //         starter.show();
        //     }
        // },
        // closeCompSubStarter() {
        //     const starter = this.$refs.compSubStarter;
        //     if (starter) {
        //         starter.hide();
        //     }
        // },
    },
}
</script>

<style lang="scss">

.compliance-item-detail {
    .compliance-item-detail-sections {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        width: 100%;
        column-gap: 1rem;
        row-gap: 1.5rem;

        .section-item {
            display: flex;
            width: 100%;
            column-gap: 0.75rem;
            row-gap: 1rem;
            &:not(:first-of-type) {
                padding-top: 1.5rem;
                border-top: 1px solid var(--gray-200);
            }

            .section-heading-outer {
                background-color: var(--gray-200);
                padding: 0.5rem 0.5rem;
                border-radius: 0.5rem;
            }

            .section-heading {
                //text-transform: uppercase;
                font-weight: 500;
                font-size: 0.9rem;
                line-height: 1;
                margin-bottom: 0.5rem;
                color: var(--gray-900);
            }

            .item-heading {
                text-transform: uppercase;
                font-weight: 500;
                font-size: 0.75rem;
                line-height: 1;
                margin-bottom: 0.5rem;
                color: var(--gray-500);
            }
            .item-text {
                font-weight: 500;
            }
        }
    }
}

</style>